import { useLingui } from '@lingui/react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import Icon from '../components/Icon';
import { apiBaseUrl } from '../config';

interface ArticleSummary {
  id: string;
  title: string;
  category: string | null;
  timestamps?: { createdAt?: string };
}

export default function NewsOverviewPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
  }, []);

  async function loadArticles(): Promise<void> {
    setLoading(true);
    try {
      const { data } = await axios.get<{ files: string[] }>(
        `${apiBaseUrl}/files`,
      );

      const blogFiles = data.files.filter((f) => f.startsWith('blogs/'));

      const articlePromises = blogFiles.map((file) =>
        axios
          .get<ArticleSummary>(`${apiBaseUrl}/files/${file}`)
          .then((res) => res.data),
      );

      setArticles(await Promise.all(articlePromises));
    } catch (err) {
      console.error('Failed to load articles', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    try {
      await axios.delete(`${apiBaseUrl}/files/blogs/${id}.json`);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to delete article', err);
    }
  }

  if (loading) {
    return (
      <div className="container-page padding-top-8">
        <p>{i18n._('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-8">
        <div className="flex justify-between items-center margin-bottom-4">
          <h1>{i18n._('news.title')}</h1>
          <Button onClick={() => navigate('/news/new')}>
            {i18n._('news.new')}
          </Button>
        </div>

        {articles.length === 0 && <p>{i18n._('news.empty')}</p>}

        <div className="flex flex-col gap-2">
          {articles.map((article) => (
            <div
              key={article.id}
              className="border border-gray-tint-6 rounded-_20 padding-3 flex justify-between items-center"
            >
              <div>
                <strong>{article.title || 'Ohne Titel'}</strong>
                {article.category && (
                  <span className="margin-left-2 text-sm">
                    {article.category}
                  </span>
                )}
              </div>

              <div className="flex gap-1">
                <Icon
                  name="icon-edit"
                  size={2}
                  onClick={() => navigate(`/news/edit/${article.id}`)}
                />
                <Icon
                  name="icon-bin"
                  size={2}
                  onClick={() => handleDelete(article.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
